// @ts-nocheck
// Right-hand Sheet with the full LiveCall row + a status timeline. The call
// object is re-read from the live page on every frame so the sheet keeps
// ticking while open; it closes itself when the call hangs up.
import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Dot, Pill } from '@/features/cti2/team/teamUi';
import { getApiCall } from '@/service/httpCall';
import { API_LIVE_CALL_CUSTOMER } from '@/service/url';
import type { LiveCall, LiveCallsQuery } from '../types';
import { fmtSpan, sinceMs, statusMeta } from '../liveCallsModel';
import { DirectionCell, RouteCell, StatusPill } from './LiveCallBits';

const CONTACT_FIELDS = [
  ['company', 'Company'],
  ['street', 'Street'],
  ['city', 'City'],
  ['state', 'State'],
  ['country', 'Country'],
];

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-x-3 gap-y-1 text-[12.5px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

interface Props {
  call: LiveCall | null;
  now: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rendered under the header (e.g. SnoopButtons for team leads). */
  actions?: React.ReactNode;
  /** Board scope ids (companyId / ccId for platform viewers) sent with the customer lookup. */
  scopeQuery?: LiveCallsQuery;
}

export default function LiveCallDetailSheet({ call, now, open, onOpenChange, actions, scopeQuery }: Props) {
  const [contact, setContact] = useState<{ callId: string; data: any } | null>(null);
  const callId = open ? call?.id : null;
  const lookup = callId && call.direction !== 'INT' && call.customerNo;

  useEffect(() => {
    if (open && !call) onOpenChange(false);
  }, [open, call, onOpenChange]);

  useEffect(() => {
    if (!lookup) return;
    let alive = true;
    getApiCall(API_LIVE_CALL_CUSTOMER(callId), scopeQuery)
      .then((d) => alive && setContact({ callId, data: d?.contact || null }))
      .catch(() => alive && setContact({ callId, data: null }));
    return () => { alive = false; };
  }, [callId, lookup]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!call) return null;
  const details = contact?.callId === call.id ? contact.data : undefined; // undefined = loading
  const meta = statusMeta(call.status);
  const steps = [{ label: 'Call started', at: call.startedAt }];
  if (call.connectedAt) steps.push({ label: 'Connected', at: call.connectedAt });
  if (call.statusSince && call.statusSince !== call.startedAt && call.statusSince !== call.connectedAt) {
    steps.push({ label: meta.label, at: call.statusSince });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <DirectionCell direction={call.direction} />
          </SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            <StatusPill call={call} now={now} />
            {details ? <Pill tone="good">Saved contact</Pill> : null}
            <span className="tabular-nums">total {fmtSpan(sinceMs(now, call.startedAt))}</span>
          </SheetDescription>
        </SheetHeader>

        {actions ? <div className="mt-3">{actions}</div> : null}

        <Separator className="my-4" />

        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Customer details</h3>
        <dl className="space-y-2">
          <Row label="Customer">
            {call.customerName ? (
              <span className="flex flex-col">
                <span className="font-medium">{call.customerName}</span>
                <span className="tabular-nums text-muted-foreground">{call.customerNo || '—'}</span>
              </span>
            ) : (
              <span className="tabular-nums font-medium">{call.customerNo || '—'}</span>
            )}
          </Row>
          {lookup ? (
            details === undefined ? (
              <Row label="Contact"><span className="text-muted-foreground">Loading…</span></Row>
            ) : details ? (
              <>
                {CONTACT_FIELDS.filter(([key]) => details[key]).map(([key, label]) => (
                  <Row key={key} label={label}>{details[key]}</Row>
                ))}
                {Object.entries(details.additional || {})
                  .map(([key, v]) => [key, Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v).trim()])
                  .filter(([, v]) => v)
                  .map(([key, v]) => (
                    <Row key={`custom-${key}`} label={key}>{v}</Row>
                  ))}
              </>
            ) : (
              <Row label="Contact"><span className="text-muted-foreground">Not a saved contact</span></Row>
            )
          ) : null}
        </dl>

        <Separator className="my-4" />

        <dl className="space-y-2">
          <Row label="DID / CLI"><span className="tabular-nums">{call.did || '—'}</span></Row>
          <Row label="Keys pressed"><span className="tabular-nums">{call.dtmf || '—'}</span></Row>
          <Row label="Source">{call.source || '—'}</Row>
          <Row label="Agent">
            {call.agent ? (
              <span className="flex flex-col">
                <span className="font-medium">{call.agent.name || `Agent ${call.agent.id}`}</span>
                <span className="text-[11px] text-muted-foreground">ext {call.agent.ext || '—'} · channel {call.agent.callId || '—'}</span>
              </span>
            ) : call.aiAgentId ? `AI agent #${call.aiAgentId}` : '—'}
          </Row>
          {call.calleeAgent ? (
            <Row label="Called agent">
              {call.calleeAgent.name || `Agent ${call.calleeAgent.id}`} · ext {call.calleeAgent.ext || '—'}
            </Row>
          ) : null}
          <Row label="Route"><RouteCell call={call} /></Row>
          {call.routingType ? <Row label="Routing">{call.routingType}</Row> : null}
          <Row label="PBX">{call.pbx}</Row>
          <Row label="Trace"><span className="font-mono text-[11px]">{call.traceId}</span></Row>
          {call.requestId ? <Row label="Request"><span className="font-mono text-[11px]">{call.requestId}</span></Row> : null}
          <Row label="Listening">{call.snoopers > 0 ? <Badge variant="secondary">{call.snoopers} supervisor(s)</Badge> : '—'}</Row>
          {call.callGroupIds?.length ? <Row label="Call groups">{call.callGroupIds.join(', ')}</Row> : null}
        </dl>

        <Separator className="my-4" />

        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Timeline</h3>
        <ol className="space-y-2">
          {steps.map((s, i) => (
            <li key={`${s.label}-${s.at}`} className="grid grid-cols-[72px_1fr] items-center gap-3 text-[12px]">
              <span className="tabular-nums text-muted-foreground">{fmtSpan(sinceMs(now, s.at))} ago</span>
              <span className="inline-flex items-center gap-2">
                <Dot tone={i === steps.length - 1 ? 'good' : 'off'} />
                {s.label}
              </span>
            </li>
          ))}
        </ol>
      </SheetContent>
    </Sheet>
  );
}
